import React, { useCallback } from 'react';
import { Edge } from 'reactflow';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import { nodeDataAutoSaveDynamic } from '../../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../../util/checkExecute';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';
import type { IntegrationProviderConfig } from '../types';

export function useThirdPartySubmit(
  provider: string,
  config: IntegrationProviderConfig | undefined,
  mode: 'pull' | 'push',
  formValues: Record<string, unknown>,
  data: Record<string, unknown>,
  edges: Edge[],
  id: string,
  flowId: string | undefined,
  setNodeState: (value: unknown) => void,
  setSaving: (saving: {
    lastSaved?: string;
    status: string;
    finished?: boolean;
    flowId?: string;
  }) => void,
  setSchema: (schema: Record<string, { type: string; required: boolean }>) => void,
  setValidationErrorFieldName: (name: string | null) => void,
  setIsLoading: (v: boolean) => void,
  setHasToExecute: (v: boolean) => void,
) {
  const setNotification = useNotificationStore(
    state => state.setNotification,
  );

  const onSubmit = useCallback(async () => {
    if (!provider || !config) {
      setNotification({
        type: NotificationType.Error,
        message: 'Please select a provider.',
      });
      return;
    }

    const hasNodeConnection =
      !!(data.integrationId as string | undefined) ||
      useStoreThirdPartyIntegration.getState().isProviderConnected(provider) ||
      !!(data.connectedByUserId as string | undefined);
    if (config.authMethod !== 'apiKey' && !hasNodeConnection) {
      setNotification({
        type: NotificationType.Error,
        message: `Please connect to ${config.name} first.`,
      });
      return;
    }

    const currentFields =
      mode === 'pull'
        ? config.pullConfig?.getFields((formValues.pullObjectType as string) ?? '') ?? []
        : config.pushConfig?.getFields((formValues.pushAction as string) ?? '') ?? [];

    const missingRequired = currentFields.find(f => {
      if (f.validation?.required !== true) return false;
      const rawVal = formValues[f.name];
      const strVal = rawVal == null ? '' : String(rawVal).trim();
      const isEmptyOrPlaceholder =
        strVal === '' ||
        (f.placeholder && strVal === String(f.placeholder).trim()) ||
        /^select\s/i.test(strVal) ||
        strVal.toLowerCase() === 'select...';
      const hasValue =
        rawVal != null &&
        (typeof rawVal !== 'string' ? true : !isEmptyOrPlaceholder);
      const connectionData = data[f.name + 'Data'];
      const hasConnection =
        f.hasConnectionPoint === true &&
        Array.isArray(connectionData) &&
        connectionData.length > 0;
      return !hasValue && !hasConnection;
    });
    if (missingRequired) {
      setValidationErrorFieldName(missingRequired.name);
      setNotification({
        type: NotificationType.Warning,
        message: `${missingRequired.label} is required.`,
      });
      return;
    }

    const isPostMessage =
      mode === 'push' && (formValues.pushAction as string) === 'post_message';
    if (isPostMessage) {
      const hasText =
        (formValues.text != null && String(formValues.text).trim() !== '') ||
        (Array.isArray(data.textData) && data.textData.length > 0);
      const hasBlockKit =
        (formValues.blockKitJson != null &&
          String(formValues.blockKitJson).trim() !== '') ||
        (Array.isArray(data.blockKitJsonData) && data.blockKitJsonData.length > 0);
      if (!hasText && !hasBlockKit) {
        setValidationErrorFieldName('text');
        setNotification({
          type: NotificationType.Warning,
          message: 'Message text or Block Kit JSON is required.',
        });
        return;
      }
    }

    if (mode === 'push' && config.pushConfig?.validateWebhookUrl) {
      const url = (formValues.webhookUrl as string) ?? '';
      const result = config.pushConfig.validateWebhookUrl(url);
      if (!result.valid) {
        setValidationErrorFieldName('webhookUrl');
        setNotification({
          type: NotificationType.Warning,
          message: result.errorMessage ?? 'Invalid webhook URL',
        });
        return;
      }
    }

    setValidationErrorFieldName(null);
    setIsLoading(true);
    try {
      const payload: Record<string, unknown> = {
        ...formValues,
        provider,
        mode,
      };
      const integrationId = data.integrationId as string | undefined;
      if (integrationId) payload.integrationId = integrationId;
      const connectedByUserId = data.connectedByUserId as string | undefined;
      if (!integrationId && connectedByUserId)
        payload.connectedByUserId = connectedByUserId;
      if (mode === 'push' && config.pushConfig) {
        payload.pushAction =
          (formValues.pushAction as string)?.trim() ||
          config.pushConfig.actions[0]?.value ||
          '';
      }
      if (mode === 'pull') {
        payload.nodeId = id;
      }
      for (const key of Object.keys(payload)) {
        if (
          key === 'provider' ||
          key === 'mode' ||
          key === 'integrationId' ||
          key === 'connectedByUserId' ||
          key === 'nodeId' ||
          key === 'pushAction'
        ) {
          continue;
        }
        const connectionData = data[key + 'Data'];
        if (Array.isArray(connectionData) && connectionData.length > 0) {
          const resolved =
            data[key] != null
              ? data[key]
              : (connectionData[0] as { text?: string })?.text ??
                connectionData[0];
          if (resolved !== undefined) {
            payload[key] = resolved;
          }
        }
      }
      if (mode === 'pull') {
        const res = await ThirdPartyIntegrationService.pullData(provider, payload);
        const output =
          typeof res?.data === 'string'
            ? res.data
            : JSON.stringify(res?.data ?? res, null, 2);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          flowId,
          setSaving,
          flowTriggerData: checkExecuteTrigger(data, id),
          changeType: 'execute',
          objectData: { output },
          setSchema,
        });
      } else {
        const res = await ThirdPartyIntegrationService.pushData(provider, payload);
        const output =
          typeof res?.data === 'string'
            ? res.data
            : JSON.stringify(res?.data ?? res, null, 2);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          flowId,
          setSaving,
          flowTriggerData: checkExecuteTrigger(data, id),
          changeType: 'execute',
          objectData: { output },
          setSchema,
        });
      }
      setTimeout(() => setHasToExecute(true), 2500);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    provider,
    config,
    mode,
    formValues,
    data,
    id,
    edges,
    setNodeState,
    flowId,
    setSaving,
    setSchema,
    setNotification,
  ]);

  return { onSubmit };
}
